import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  ArrowLeft,
  ArrowUpRight,
  Hammer,
  Stethoscope,
  Wrench,
  Store,
  Scissors,
  Building2,
  HardHat,
  Star,
} from 'lucide-react';
import { Eyebrow } from '../../design-system/pds';
import { MarketingPageHead } from '../MarketingPageHead';
import { listReviews, reviewSlug, type Review } from '../api';

/**
 * CustomerDetailPage
 * --------------------------------------------------------------
 * /customers/:slug — single customer story.
 *
 * Resolves the slug by fetching the full published-review list and
 * matching `reviewSlug(r)` against the URL slug. If no match, renders
 * the honest "story under construction" shell (no fabricated content).
 */

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const SECTOR_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  studio: Hammer,
  clinic: Stethoscope,
  trades: Wrench,
  shop:   Store,
  retail: Store,
  salon:  Scissors,
  agency: Building2,
};

function iconForSector(sector: string | null | undefined): React.ComponentType<{ className?: string }> {
  if (!sector) return HardHat;
  const s = sector.toLowerCase();
  for (const key of Object.keys(SECTOR_ICONS)) {
    if (s.includes(key)) return SECTOR_ICONS[key];
  }
  return HardHat;
}

export function CustomerDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const [loading, setLoading] = React.useState(true);
  const [review, setReview] = React.useState<Review | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!slug) { setLoading(false); return; }
    listReviews({ sort: 'default' })
      .then((rs) => {
        if (cancelled) return;
        const match = rs.find((r) => reviewSlug(r) === slug) ?? null;
        setReview(match);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setReview(null);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return <LoadingShell slug={slug} />;
  if (review)  return <StoryView review={review} />;
  return <ComingSoonShell slug={slug} />;
}

/* ─────────────────────────────────────────────────────────────
 * REAL CUSTOMER STORY
 * ───────────────────────────────────────────────────────────── */

function StoryView({ review }: { review: Review }) {
  const Icon = iconForSector(review.sector);
  const company = review.company || review.name;

  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title={`${company} on Worqly`}
        description={review.body.slice(0, 155)}
        image={review.avatar_url ?? undefined}
        path={`/customers/${reviewSlug(review)}`}
      />

      <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={STAGGER}>
            <motion.div variants={FADE_UP}>
              <Link
                to="/customers"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink/55 dark:text-white/55 hover:text-coral dark:hover:text-coral-400 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                All customer stories
              </Link>
            </motion.div>

            <motion.div variants={FADE_UP} className="mt-6 flex items-center gap-4">
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 shadow-card">
                <Icon className="h-7 w-7" />
              </span>
              <div className="min-w-0">
                <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-ink/55 dark:text-white/55">
                  {[review.sector, review.city].filter(Boolean).join(' · ') || 'Operator'}
                </p>
                <h1 className="mt-1 font-light text-display-lg text-ink dark:text-white tracking-[-0.03em] leading-[1.05] text-balance">
                  {company}
                </h1>
              </div>
            </motion.div>

            {review.rating != null && (
              <motion.div variants={FADE_UP} className="mt-6 inline-flex items-center gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    className={
                      'h-4 w-4 ' +
                      (i < (review.rating ?? 0) ? 'fill-coral text-coral' : 'text-ink/15 dark:text-white/15')
                    }
                    strokeWidth={1.5}
                  />
                ))}
              </motion.div>
            )}

            <motion.blockquote
              variants={FADE_UP}
              className="mt-8 text-[24px] lg:text-[28px] font-light leading-[1.35] text-ink dark:text-white tracking-[-0.015em] text-balance"
            >
              <span className="font-serif italic text-coral text-[36px] leading-none align-top mr-1">"</span>
              {review.body}
              <span className="font-serif italic text-coral text-[36px] leading-none align-bottom ml-0.5">"</span>
            </motion.blockquote>

            <motion.div
              variants={FADE_UP}
              className="mt-8 flex items-center gap-4"
            >
              {review.avatar_url ? (
                <img
                  src={review.avatar_url}
                  alt={review.name}
                  className="h-12 w-12 rounded-full object-cover ring-1 ring-ink/8 dark:ring-white/10"
                />
              ) : (
                <span
                  aria-hidden
                  className="h-12 w-12 rounded-full bg-coral text-white flex items-center justify-center text-[16px] font-bold ring-1 ring-coral/30"
                >
                  {review.name.charAt(0).toUpperCase()}
                </span>
              )}
              <div className="min-w-0">
                <p className="text-[15px] font-semibold text-ink dark:text-white truncate">{review.name}</p>
                {review.role && (
                  <p className="text-[13px] text-ink/55 dark:text-white/55 truncate">
                    {review.role}{review.company ? `, ${review.company}` : ''}
                  </p>
                )}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-pearl-50/60 dark:bg-white/[0.02] border-y border-ink/5 dark:border-white/5">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5">
          {[
            { l: 'Sector',  v: review.sector || '—' },
            { l: 'City',    v: review.city || '—' },
            { l: 'Customer since', v: new Date(review.created_at).toLocaleDateString(undefined, { year: 'numeric', month: 'short' }) },
          ].map((c) => (
            <div
              key={c.l}
              className="rounded-3xl p-6 bg-white/85 dark:bg-white/[0.04] ring-1 ring-ink/5 dark:ring-white/10 shadow-card"
            >
              <Eyebrow>{c.l}</Eyebrow>
              <p className="mt-2 text-[16px] font-semibold text-ink dark:text-white truncate">{c.v}</p>
            </div>
          ))}
        </div>
      </section>

      <BottomCTA />
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * LOADING SKELETON
 * ───────────────────────────────────────────────────────────── */

function LoadingShell({ slug }: { slug: string | undefined }) {
  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead title={slug ? `${slug} story` : 'Customer'} description="Loading the customer story…" />
      <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-6">
          <div className="h-3 w-24 rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
          <div className="h-12 w-2/3 rounded-2xl bg-ink/10 dark:bg-white/10 animate-pulse" />
          <div className="h-3 w-1/3 rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
          <div className="space-y-2 mt-8">
            <div className="h-4 w-full rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
            <div className="h-4 w-5/6 rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
            <div className="h-4 w-3/4 rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
          </div>
        </div>
      </section>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * COMING-SOON SHELL — when the slug doesn't match a real review
 * ───────────────────────────────────────────────────────────── */

function ComingSoonShell({ slug }: { slug: string | undefined }) {
  const Icon = iconForSector(slug);
  const label = slug ? humanize(slug) : 'Customer';

  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title={`${label} story coming soon`}
        description="Customer story under construction — published once the quote is approved."
        noindex
      />

      <section className="relative pt-12 pb-10 lg:pt-20 lg:pb-14">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={STAGGER}>
            <motion.div variants={FADE_UP}>
              <Link
                to="/customers"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink/55 dark:text-white/55 hover:text-coral dark:hover:text-coral-400 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                All customer stories
              </Link>
            </motion.div>

            <motion.div variants={FADE_UP} className="mt-6 flex items-center justify-center">
              <span className="inline-flex h-16 w-16 items-center justify-center rounded-3xl bg-coral/10 text-coral-600 dark:bg-coral/15 dark:text-coral-400 shadow-card">
                <Icon className="h-7 w-7" />
              </span>
            </motion.div>

            <motion.div variants={FADE_UP} className="mt-6">
              <span
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.10em]
                  bg-coral/10 text-coral-700 ring-1 ring-coral/20
                  dark:bg-coral/15 dark:text-coral-300 dark:ring-coral/25"
              >
                Story under construction
              </span>
            </motion.div>

            <motion.h1
              variants={FADE_UP}
              className="mt-5 font-light text-display-lg text-ink dark:text-white tracking-[-0.035em] leading-[1.05] text-balance"
            >
              {label} story{' '}
              <span className="italic font-serif text-coral">coming soon.</span>
            </motion.h1>

            <motion.p
              variants={FADE_UP}
              className="mt-5 text-body-lg text-ink/65 dark:text-white/65 max-w-xl mx-auto text-pretty"
            >
              We publish customer stories only after the quote is approved by name and the
              numbers are reconciled against the workspace.
            </motion.p>

            <motion.div variants={FADE_UP} className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link
                to="/customers"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-coral text-white text-[14.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to customers
              </Link>
              <a
                href="mailto:hello@worqly.io?subject=Notify%20me%20when%20customer%20stories%20publish"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white text-ink text-[14.5px] font-semibold shadow-pill ring-1 ring-ink/10 hover:bg-pearl-50 dark:bg-white/10 dark:text-white dark:ring-white/10 dark:hover:bg-white/15 transition-colors focus-coral"
              >
                Notify me when it's live
                <ArrowUpRight className="h-4 w-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <BottomCTA />
    </div>
  );
}

function BottomCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">Start free</p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Be the next story on this page.
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Seven days free, no card to start. If Worqly saves your team a Friday a month,
                we'd love to tell that story together.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/customers"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                All stories
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function humanize(slug: string): string {
  return slug
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}
